import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, RotateCcw, Volume2, VolumeX, Trophy, Star, Lock, Unlock, Eye } from 'lucide-react';

// =============================================================================
// WAGGLE DANCE GAME - A relaxing bee-themed pattern tracing game
// =============================================================================
// Challenge is REMEMBERING THE ORDER the bee traces, not geometric complexity
// =============================================================================

// =============================================================================
// SOUND SYSTEM - Web Audio API for bee-themed sounds
// =============================================================================
class SoundSystem {
  constructor() {
    this.audioContext = null;
    this.enabled = true;
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
    } catch (e) {
      console.log('Web Audio not supported');
    }
  }

  setEnabled(enabled) {
    this.enabled = enabled;
  }

  // Gentle bee buzz - plays while guide bee is moving
  playBuzz(duration = 0.1) {
    if (!this.enabled || !this.audioContext) return;
    this.init();
    
    const now = this.audioContext.currentTime;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(180, now);
    osc.frequency.linearRampToValueAtTime(220, now + duration / 2);
    osc.frequency.linearRampToValueAtTime(180, now + duration);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.08, now + 0.02);
    gain.gain.linearRampToValueAtTime(0.05, now + duration - 0.02);
    gain.gain.linearRampToValueAtTime(0, now + duration);
    
    osc.connect(gain);
    gain.connect(this.audioContext.destination);
    osc.start(now);
    osc.stop(now + duration);
  }

  // Play a chord - multiple notes at once
  playChord(frequencies, volume = 0.06, duration = 1.0) {
    if (!this.enabled || !this.audioContext) return;
    this.init();
    
    const now = this.audioContext.currentTime;
    
    frequencies.forEach(freq => {
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
      
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      osc.start(now);
      osc.stop(now + duration + 0.1);
    });
  }

  // Level passed - warm major chord (C major)
  playSuccess() {
    // C4, E4, G4 - a warm C major chord
    this.playChord([262, 330, 392], 0.07, 0.8);
  }

  // Encouraging "try again" sound - soft descending tone
  playTryAgain() {
    if (!this.enabled || !this.audioContext) return;
    this.init();
    
    const now = this.audioContext.currentTime;
    const osc = this.audioContext.createOscillator();
    const gain = this.audioContext.createGain();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, now);
    osc.frequency.linearRampToValueAtTime(330, now + 0.3);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.1, now + 0.05);
    gain.gain.linearRampToValueAtTime(0, now + 0.35);
    
    osc.connect(gain);
    gain.connect(this.audioContext.destination);
    osc.start(now);
    osc.stop(now + 0.4);
  }

  // Celebration - brighter major chord (G major)
  playCelebration() {
    // G4, B4, D5 - bright G major chord
    this.playChord([392, 494, 587], 0.08, 1.0);
  }

  // Mode complete - fuller, richer chord (C major 7th with octave)
  playVictory() {
    // C4, E4, G4, B4, C5 - rich C major 7th chord
    this.playChord([262, 330, 392, 494, 523], 0.06, 1.2);
  }

  // Click sound - short percussive click using noise
  playClick() {
    if (!this.enabled || !this.audioContext) return;
    this.init();
    
    const now = this.audioContext.currentTime;
    
    // Create a short noise burst for click
    const bufferSize = this.audioContext.sampleRate * 0.02; // 20ms
    const buffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Generate noise with quick decay
    for (let i = 0; i < bufferSize; i++) {
      const decay = 1 - (i / bufferSize);
      data[i] = (Math.random() * 2 - 1) * decay * decay;
    }
    
    const noise = this.audioContext.createBufferSource();
    noise.buffer = buffer;
    
    // Filter to make it more "clicky" and less harsh
    const filter = this.audioContext.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1500;
    filter.Q.value = 1;
    
    const gain = this.audioContext.createGain();
    gain.gain.value = 0.3;
    
    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.audioContext.destination);
    
    noise.start(now);
    noise.stop(now + 0.03);
  }
}

// Global sound instance
const sounds = new SoundSystem();

const COLORS = {
  background: '#FDFBF7',
  navy: '#1e3a5f',
  amber: '#f59e0b',
  amberLight: '#fcd34d',
  amberDark: '#b45309',
};

// =============================================================================
// PATTERN GENERATORS - Focus on SEQUENCE MEMORY challenge
// =============================================================================

// Simple circle - clockwise starting from right
function generateCircle() {
  const points = [];
  for (let i = 0; i <= 24; i++) {
    const t = (i / 24) * Math.PI * 2;
    points.push({
      x: 0.5 + 0.18 * Math.cos(t),
      y: 0.5 + 0.18 * Math.sin(t)
    });
  }
  return points;
}

// Tall oval - visually distinct from circle
function generateOvalTall() {
  const points = [];
  for (let i = 0; i <= 24; i++) {
    const t = (i / 24) * Math.PI * 2;
    points.push({
      x: 0.5 + 0.12 * Math.cos(t),
      y: 0.5 + 0.22 * Math.sin(t)
    });
  }
  return points;
}

// Wide oval
function generateOvalWide() {
  const points = [];
  for (let i = 0; i <= 24; i++) {
    const t = (i / 24) * Math.PI * 2;
    points.push({
      x: 0.5 + 0.22 * Math.cos(t),
      y: 0.5 + 0.12 * Math.sin(t)
    });
  }
  return points;
}

// Simple wave left to right
function generateWave() {
  const points = [];
  for (let i = 0; i <= 30; i++) {
    const t = i / 30;
    points.push({
      x: 0.15 + t * 0.7,
      y: 0.5 + Math.sin(t * Math.PI * 2) * 0.12
    });
  }
  return points;
}

// Heart shape
function generateHeart() {
  const points = [];
  for (let i = 0; i <= 30; i++) {
    const t = (i / 30) * Math.PI * 2;
    const x = 0.5 + 0.14 * Math.pow(Math.sin(t), 3);
    const y = 0.47 - 0.1 * Math.cos(t) + 0.04 * Math.cos(2*t) + 0.015 * Math.cos(3*t);
    points.push({ x, y });
  }
  return points;
}

// Figure-8 vertical
function generateFigure8() {
  const points = [];
  for (let i = 0; i <= 30; i++) {
    const t = (i / 30) * Math.PI * 2;
    points.push({
      x: 0.5 + 0.14 * Math.sin(t),
      y: 0.5 + 0.18 * Math.sin(t * 2)
    });
  }
  return points;
}

// Infinity (horizontal 8)
function generateInfinity() {
  const points = [];
  for (let i = 0; i <= 30; i++) {
    const t = (i / 30) * Math.PI * 2;
    points.push({
      x: 0.5 + 0.2 * Math.cos(t) / (1 + Math.pow(Math.sin(t), 2)),
      y: 0.5 + 0.12 * Math.sin(t) * Math.cos(t) / (1 + Math.pow(Math.sin(t), 2))
    });
  }
  return points;
}

// Triangle
function generateTriangle() {
  const corners = [
    { x: 0.5, y: 0.28 }, { x: 0.72, y: 0.68 },
    { x: 0.28, y: 0.68 }, { x: 0.5, y: 0.28 }
  ];
  const points = [];
  for (let i = 0; i < corners.length - 1; i++) {
    for (let j = 0; j <= 10; j++) {
      const t = j / 10;
      points.push({
        x: corners[i].x + (corners[i+1].x - corners[i].x) * t,
        y: corners[i].y + (corners[i+1].y - corners[i].y) * t
      });
    }
  }
  return points;
}

// Square clockwise
function generateSquare() {
  const corners = [
    { x: 0.32, y: 0.32 }, { x: 0.68, y: 0.32 },
    { x: 0.68, y: 0.68 }, { x: 0.32, y: 0.68 },
    { x: 0.32, y: 0.32 }
  ];
  const points = [];
  for (let i = 0; i < corners.length - 1; i++) {
    for (let j = 0; j <= 8; j++) {
      const t = j / 8;
      points.push({
        x: corners[i].x + (corners[i+1].x - corners[i].x) * t,
        y: corners[i].y + (corners[i+1].y - corners[i].y) * t
      });
    }
  }
  return points;
}

// Spiral outward
function generateSpiralOut() {
  const points = [];
  for (let i = 0; i <= 40; i++) {
    const t = (i / 40) * Math.PI * 4;
    const r = 0.04 + (i / 40) * 0.16;
    points.push({
      x: 0.5 + r * Math.cos(t),
      y: 0.5 + r * Math.sin(t)
    });
  }
  return points;
}

// =============================================================================
// TWO CIRCLE PATTERNS - Different orders (user's favorite type!)
// =============================================================================

// Two circles: Left then Right (intuitive)
function generateTwoCirclesLR() {
  const points = [];
  // Left circle clockwise
  for (let i = 0; i <= 16; i++) {
    const t = (i / 16) * Math.PI * 2;
    points.push({ x: 0.32 + 0.12 * Math.cos(t), y: 0.5 + 0.12 * Math.sin(t) });
  }
  // Right circle clockwise
  for (let i = 0; i <= 16; i++) {
    const t = (i / 16) * Math.PI * 2;
    points.push({ x: 0.68 + 0.12 * Math.cos(t), y: 0.5 + 0.12 * Math.sin(t) });
  }
  return points;
}

// Two circles: Right then Left (reverse)
function generateTwoCirclesRL() {
  const points = [];
  // Right circle first
  for (let i = 0; i <= 16; i++) {
    const t = (i / 16) * Math.PI * 2;
    points.push({ x: 0.68 + 0.12 * Math.cos(t), y: 0.5 + 0.12 * Math.sin(t) });
  }
  // Left circle second
  for (let i = 0; i <= 16; i++) {
    const t = (i / 16) * Math.PI * 2;
    points.push({ x: 0.32 + 0.12 * Math.cos(t), y: 0.5 + 0.12 * Math.sin(t) });
  }
  return points;
}

// Two circles: Left CW, Right CCW (mixed directions)
function generateTwoCirclesMixed() {
  const points = [];
  // Left circle clockwise
  for (let i = 0; i <= 16; i++) {
    const t = (i / 16) * Math.PI * 2;
    points.push({ x: 0.32 + 0.12 * Math.cos(t), y: 0.5 + 0.12 * Math.sin(t) });
  }
  // Right circle COUNTER-clockwise
  for (let i = 16; i >= 0; i--) {
    const t = (i / 16) * Math.PI * 2;
    points.push({ x: 0.68 + 0.12 * Math.cos(t), y: 0.5 + 0.12 * Math.sin(t) });
  }
  return points;
}

// =============================================================================
// THREE CIRCLE PATTERNS - More order variations
// =============================================================================

// Three circles: Left, Middle, Right (intuitive)
function generateThreeCirclesLMR() {
  const centers = [{ x: 0.25, y: 0.5 }, { x: 0.5, y: 0.5 }, { x: 0.75, y: 0.5 }];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 14; i++) {
      const t = (i / 14) * Math.PI * 2;
      points.push({ x: c.x + 0.1 * Math.cos(t), y: c.y + 0.1 * Math.sin(t) });
    }
  }
  return points;
}

// Three circles: Left, Right, Middle (skip middle first)
function generateThreeCirclesLRM() {
  const centers = [{ x: 0.25, y: 0.5 }, { x: 0.75, y: 0.5 }, { x: 0.5, y: 0.5 }];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 14; i++) {
      const t = (i / 14) * Math.PI * 2;
      points.push({ x: c.x + 0.1 * Math.cos(t), y: c.y + 0.1 * Math.sin(t) });
    }
  }
  return points;
}

// Three circles: Middle, Left, Right (start in middle)
function generateThreeCirclesMLR() {
  const centers = [{ x: 0.5, y: 0.5 }, { x: 0.25, y: 0.5 }, { x: 0.75, y: 0.5 }];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 14; i++) {
      const t = (i / 14) * Math.PI * 2;
      points.push({ x: c.x + 0.1 * Math.cos(t), y: c.y + 0.1 * Math.sin(t) });
    }
  }
  return points;
}

// Three circles: Right, Middle, Left (backwards)
function generateThreeCirclesRML() {
  const centers = [{ x: 0.75, y: 0.5 }, { x: 0.5, y: 0.5 }, { x: 0.25, y: 0.5 }];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 14; i++) {
      const t = (i / 14) * Math.PI * 2;
      points.push({ x: c.x + 0.1 * Math.cos(t), y: c.y + 0.1 * Math.sin(t) });
    }
  }
  return points;
}

// Three circles: Middle, Right, Left (tricky)
function generateThreeCirclesMRL() {
  const centers = [{ x: 0.5, y: 0.5 }, { x: 0.75, y: 0.5 }, { x: 0.25, y: 0.5 }];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 14; i++) {
      const t = (i / 14) * Math.PI * 2;
      points.push({ x: c.x + 0.1 * Math.cos(t), y: c.y + 0.1 * Math.sin(t) });
    }
  }
  return points;
}

// Three vertical circles: Top, Middle, Bottom
function generateThreeCirclesVert() {
  const centers = [{ x: 0.5, y: 0.28 }, { x: 0.5, y: 0.5 }, { x: 0.5, y: 0.72 }];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 14; i++) {
      const t = (i / 14) * Math.PI * 2;
      points.push({ x: c.x + 0.1 * Math.cos(t), y: c.y + 0.1 * Math.sin(t) });
    }
  }
  return points;
}

// Three vertical circles: Bottom, Top, Middle (skip around)
function generateThreeCirclesBTM() {
  const centers = [{ x: 0.5, y: 0.72 }, { x: 0.5, y: 0.28 }, { x: 0.5, y: 0.5 }];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 14; i++) {
      const t = (i / 14) * Math.PI * 2;
      points.push({ x: c.x + 0.1 * Math.cos(t), y: c.y + 0.1 * Math.sin(t) });
    }
  }
  return points;
}

// =============================================================================
// FOUR CIRCLE PATTERNS - Even more complex orders
// =============================================================================

// Four circles in corners: TL, TR, BR, BL (clockwise)
function generateFourCirclesCW() {
  const centers = [
    { x: 0.3, y: 0.3 }, { x: 0.7, y: 0.3 },
    { x: 0.7, y: 0.7 }, { x: 0.3, y: 0.7 }
  ];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 12; i++) {
      const t = (i / 12) * Math.PI * 2;
      points.push({ x: c.x + 0.09 * Math.cos(t), y: c.y + 0.09 * Math.sin(t) });
    }
  }
  return points;
}

// Four circles: TL, BR, TR, BL (diagonals)
function generateFourCirclesDiag() {
  const centers = [
    { x: 0.3, y: 0.3 }, { x: 0.7, y: 0.7 },
    { x: 0.7, y: 0.3 }, { x: 0.3, y: 0.7 }
  ];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 12; i++) {
      const t = (i / 12) * Math.PI * 2;
      points.push({ x: c.x + 0.09 * Math.cos(t), y: c.y + 0.09 * Math.sin(t) });
    }
  }
  return points;
}

// Four circles: TR, BL, TL, BR (cross pattern)
function generateFourCirclesCross() {
  const centers = [
    { x: 0.7, y: 0.3 }, { x: 0.3, y: 0.7 },
    { x: 0.3, y: 0.3 }, { x: 0.7, y: 0.7 }
  ];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 12; i++) {
      const t = (i / 12) * Math.PI * 2;
      points.push({ x: c.x + 0.09 * Math.cos(t), y: c.y + 0.09 * Math.sin(t) });
    }
  }
  return points;
}

// Four circles: BR, TL, BL, TR (reverse diagonal)
function generateFourCirclesRevDiag() {
  const centers = [
    { x: 0.7, y: 0.7 }, { x: 0.3, y: 0.3 },
    { x: 0.3, y: 0.7 }, { x: 0.7, y: 0.3 }
  ];
  const points = [];
  for (const c of centers) {
    for (let i = 0; i <= 12; i++) {
      const t = (i / 12) * Math.PI * 2;
      points.push({ x: c.x + 0.09 * Math.cos(t), y: c.y + 0.09 * Math.sin(t) });
    }
  }
  return points;
}

// =============================================================================
// OTHER SEQUENCE-CHALLENGING PATTERNS
// =============================================================================

// Wave that goes back and forth
function generateWaveBackForth() {
  const points = [];
  // Go right
  for (let i = 0; i <= 15; i++) {
    const t = i / 30;
    points.push({
      x: 0.15 + t * 0.7,
      y: 0.5 + Math.sin(t * Math.PI * 2) * 0.1
    });
  }
  // Go back left
  for (let i = 15; i >= 8; i--) {
    const t = i / 30;
    points.push({
      x: 0.15 + t * 0.7,
      y: 0.5 + Math.sin(t * Math.PI * 2) * 0.1
    });
  }
  // Finish going right
  for (let i = 8; i <= 30; i++) {
    const t = i / 30;
    points.push({
      x: 0.15 + t * 0.7,
      y: 0.5 + Math.sin(t * Math.PI * 2) * 0.1
    });
  }
  return points;
}

// Square with diagonal skip (TL -> BR -> TR -> BL)
function generateSquareSkip() {
  const corners = [
    { x: 0.32, y: 0.32 }, { x: 0.68, y: 0.68 },
    { x: 0.68, y: 0.32 }, { x: 0.32, y: 0.68 },
    { x: 0.32, y: 0.32 }
  ];
  const points = [];
  for (let i = 0; i < corners.length - 1; i++) {
    for (let j = 0; j <= 8; j++) {
      const t = j / 8;
      points.push({
        x: corners[i].x + (corners[i+1].x - corners[i].x) * t,
        y: corners[i].y + (corners[i+1].y - corners[i].y) * t
      });
    }
  }
  return points;
}

// 5-point star skip (1->3->5->2->4)
function generateStarSkip() {
  const starPoints = [];
  for (let i = 0; i < 5; i++) {
    const angle = (i / 5) * Math.PI * 2 - Math.PI / 2;
    starPoints.push({
      x: 0.5 + 0.2 * Math.cos(angle),
      y: 0.5 + 0.2 * Math.sin(angle)
    });
  }
  const order = [0, 2, 4, 1, 3, 0];
  const points = [];
  for (let i = 0; i < order.length - 1; i++) {
    const from = starPoints[order[i]];
    const to = starPoints[order[i + 1]];
    for (let j = 0; j <= 8; j++) {
      const t = j / 8;
      points.push({
        x: from.x + (to.x - from.x) * t,
        y: from.y + (to.y - from.y) * t
      });
    }
  }
  return points;
}

// Circle counter-clockwise
function generateCircleCCW() {
  const points = [];
  for (let i = 24; i >= 0; i--) {
    const t = (i / 24) * Math.PI * 2;
    points.push({
      x: 0.5 + 0.18 * Math.cos(t),
      y: 0.5 + 0.18 * Math.sin(t)
    });
  }
  return points;
}

// Spiral that goes out then comes back in
function generateSpiralOutIn() {
  const points = [];
  // Out
  for (let i = 0; i <= 25; i++) {
    const t = (i / 25) * Math.PI * 3;
    const r = 0.04 + (i / 25) * 0.14;
    points.push({ x: 0.5 + r * Math.cos(t), y: 0.5 + r * Math.sin(t) });
  }
  // Back in
  for (let i = 25; i >= 0; i--) {
    const t = (i / 25) * Math.PI * 3 + Math.PI;
    const r = 0.04 + (i / 25) * 0.14;
    points.push({ x: 0.5 + r * Math.cos(t), y: 0.5 + r * Math.sin(t) });
  }
  return points;
}

// =============================================================================
// DIFFICULTY MODES - 10 patterns each
// =============================================================================

const EASY_PATTERNS = [
  { name: "Circle", points: generateCircle(), requiredAccuracy: 40, guideSpeed: 2800 },
  { name: "Tall Oval", points: generateOvalTall(), requiredAccuracy: 40, guideSpeed: 2800 },
  { name: "Wave", points: generateWave(), requiredAccuracy: 42, guideSpeed: 2900 },
  { name: "Triangle", points: generateTriangle(), requiredAccuracy: 42, guideSpeed: 2900 },
  { name: "Heart", points: generateHeart(), requiredAccuracy: 44, guideSpeed: 3000 },
  // -- Interlude --
  { name: "Square", points: generateSquare(), requiredAccuracy: 44, guideSpeed: 3000 },
  { name: "Figure 8", points: generateFigure8(), requiredAccuracy: 45, guideSpeed: 3100 },
  { name: "Wide Oval", points: generateOvalWide(), requiredAccuracy: 45, guideSpeed: 3000 },
  { name: "Infinity", points: generateInfinity(), requiredAccuracy: 46, guideSpeed: 3100 },
  { name: "Spiral Out", points: generateSpiralOut(), requiredAccuracy: 46, guideSpeed: 3200 },
];

const MEDIUM_PATTERNS = [
  { name: "Two Loops L→R", points: generateTwoCirclesLR(), requiredAccuracy: 44, guideSpeed: 3000 },
  { name: "Two Loops R→L", points: generateTwoCirclesRL(), requiredAccuracy: 45, guideSpeed: 3000 },
  { name: "Two Mixed", points: generateTwoCirclesMixed(), requiredAccuracy: 46, guideSpeed: 3100 },
  { name: "Three L→M→R", points: generateThreeCirclesLMR(), requiredAccuracy: 46, guideSpeed: 3200 },
  { name: "Three L→R→M", points: generateThreeCirclesLRM(), requiredAccuracy: 47, guideSpeed: 3200 },
  // -- Interlude --
  { name: "Circle Reverse", points: generateCircleCCW(), requiredAccuracy: 45, guideSpeed: 3000 },
  { name: "Three R→M→L", points: generateThreeCirclesRML(), requiredAccuracy: 48, guideSpeed: 3300 },
  { name: "Vertical Loops", points: generateThreeCirclesVert(), requiredAccuracy: 48, guideSpeed: 3300 },
  { name: "Square Skip", points: generateSquareSkip(), requiredAccuracy: 49, guideSpeed: 3400 },
  { name: "Wave Back-Forth", points: generateWaveBackForth(), requiredAccuracy: 50, guideSpeed: 3400 },
];

const DIFFICULT_PATTERNS = [
  { name: "Three M→L→R", points: generateThreeCirclesMLR(), requiredAccuracy: 48, guideSpeed: 3200 },
  { name: "Three M→R→L", points: generateThreeCirclesMRL(), requiredAccuracy: 49, guideSpeed: 3300 },
  { name: "Three B→T→M", points: generateThreeCirclesBTM(), requiredAccuracy: 50, guideSpeed: 3300 },
  { name: "Four Clockwise", points: generateFourCirclesCW(), requiredAccuracy: 50, guideSpeed: 3400 },
  { name: "Four Diagonal", points: generateFourCirclesDiag(), requiredAccuracy: 51, guideSpeed: 3500 },
  // -- Interlude --
  { name: "Four Cross", points: generateFourCirclesCross(), requiredAccuracy: 52, guideSpeed: 3500 },
  { name: "Four Rev-Diag", points: generateFourCirclesRevDiag(), requiredAccuracy: 53, guideSpeed: 3600 },
  { name: "Star Skip", points: generateStarSkip(), requiredAccuracy: 52, guideSpeed: 3600 },
  { name: "Spiral Out-In", points: generateSpiralOutIn(), requiredAccuracy: 54, guideSpeed: 3700 },
  { name: "Memory Test", points: generateFourCirclesCross(), requiredAccuracy: 55, guideSpeed: 3800 },
];

const MODE_CONFIG = {
  easy: { patterns: EASY_PATTERNS, color: '#22c55e', name: 'Easy', icon: '🌱' },
  medium: { patterns: MEDIUM_PATTERNS, color: '#f59e0b', name: 'Medium', icon: '🌻' },
  difficult: { patterns: DIFFICULT_PATTERNS, color: '#ef4444', name: 'Difficult', icon: '🔥' },
};

// =============================================================================
// BEE COMPONENT - Bee faces RIGHT (positive X) so it moves head-first
// =============================================================================
const Bee = ({ x, y, size = 30, angle = 0, isWaggling = false, opacity = 1, animTime = 0 }) => {
  const waggleOffset = isWaggling ? Math.sin(animTime / 50) * 3 : 0;
  const wingAngle = isWaggling ? Math.sin(animTime / 30) * 20 : 0;
  
  // Bee is drawn facing RIGHT (positive X direction):
  // - Head at positive X (right side)
  // - Stinger at negative X (left side)
  // This ensures the bee moves head-first when following the path
  return (
    <g transform={`translate(${x + waggleOffset}, ${y}) rotate(${angle})`} style={{ opacity }}>
      {/* Body */}
      <ellipse cx="0" cy="0" rx={size * 0.6} ry={size * 0.4} fill={COLORS.amber} />
      {/* Stripes */}
      <rect x={-size * 0.15} y={-size * 0.4} width={size * 0.1} height={size * 0.8} fill={COLORS.navy} />
      <rect x={size * 0.1} y={-size * 0.4} width={size * 0.1} height={size * 0.8} fill={COLORS.navy} />
      {/* Head - now at positive X (right side) */}
      <circle cx={size * 0.5} cy="0" r={size * 0.25} fill={COLORS.navy} />
      {/* Eyes - on the head */}
      <circle cx={size * 0.55} cy={-size * 0.1} r={size * 0.08} fill="white" />
      <circle cx={size * 0.55} cy={size * 0.1} r={size * 0.08} fill="white" />
      {/* Wings */}
      <ellipse cx={0} cy={-size * 0.45} rx={size * 0.35} ry={size * 0.2} fill="rgba(255,255,255,0.7)" transform={`rotate(${wingAngle})`} />
      <ellipse cx={0} cy={size * 0.45} rx={size * 0.35} ry={size * 0.2} fill="rgba(255,255,255,0.7)" transform={`rotate(${-wingAngle})`} />
      {/* Stinger - now at negative X (left/back side) */}
      <polygon points={`${-size * 0.6},0 ${-size * 0.8},-${size * 0.1} ${-size * 0.8},${size * 0.1}`} fill={COLORS.amberDark} />
    </g>
  );
};

// =============================================================================
// CELEBRATION SCENE
// =============================================================================
const generateCongaLineBees = (canvasWidth, canvasHeight) => {
  const bees = [];
  const numBees = 10;
  const startY = canvasHeight * 0.78;
  
  for (let i = 0; i < numBees; i++) {
    const t = i / (numBees - 1);
    const x = 40 + t * (canvasWidth - 80);
    const waveY = Math.sin(t * Math.PI * 2) * 25;
    bees.push({
      id: i,
      baseX: x,
      baseY: startY + waveY,
      x: x,
      y: startY + waveY,
      size: 22 + (i % 3) * 3,
      baseAngle: 0,
      angle: 0,
      phaseOffset: i * 0.4,
    });
  }
  return bees;
};

const CelebrationScene = ({ canvasWidth, canvasHeight, onComplete, levelNumber, modeName }) => {
  const [bees, setBees] = useState(() => generateCongaLineBees(canvasWidth, canvasHeight));
  const [progress, setProgress] = useState(0);
  const [animTime, setAnimTime] = useState(0);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);
  
  useEffect(() => {
    startTimeRef.current = Date.now();
    const duration = 3500;
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const p = Math.min(elapsed / duration, 1);
      setProgress(p);
      setAnimTime(now);
      
      setBees(prevBees => 
        prevBees.map((bee) => {
          const wavePhase = now / 180 + bee.phaseOffset;
          const wobbleY = Math.sin(wavePhase) * 6;
          const angleWobble = Math.sin(wavePhase * 1.3) * 12;
          return { ...bee, y: bee.baseY + wobbleY, angle: bee.baseAngle + angleWobble };
        })
      );
      
      if (p < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setTimeout(onComplete, 300);
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [onComplete]);
  
  return (
    <svg width={canvasWidth} height={canvasHeight} style={{ background: 'linear-gradient(180deg, #fffbeb 0%, #fef3c7 100%)' }}>
      <defs>
        <pattern id="honeycomb" width="50" height="43.3" patternUnits="userSpaceOnUse">
          <polygon points="25,0 50,14.4 50,43.3 25,57.7 0,43.3 0,14.4" fill="none" stroke={COLORS.amber} strokeWidth="1" opacity="0.15" transform="translate(0, -14.4)" />
        </pattern>
        <radialGradient id="celebrationGlow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor={COLORS.amberLight} stopOpacity="0.4" />
          <stop offset="100%" stopColor="#fef3c7" stopOpacity="0" />
        </radialGradient>
      </defs>
      <rect width={canvasWidth} height={canvasHeight} fill="url(#honeycomb)" />
      <rect width={canvasWidth} height={canvasHeight} fill="url(#celebrationGlow)" />
      
      {/* Honey drips */}
      <g>
        <path d={`M 0 0 L ${canvasWidth} 0 L ${canvasWidth} 35 Q ${canvasWidth * 0.5} 55 0 35 Z`} fill={COLORS.amber} opacity="0.9" />
        {[0.2, 0.4, 0.6, 0.8].map((pos, i) => {
          const dripX = canvasWidth * pos;
          const dripLength = 30 + Math.sin(animTime / 350 + i) * 10;
          return (
            <ellipse key={i} cx={dripX} cy={45 + dripLength} rx={8} ry={12} fill={COLORS.amber} opacity="0.85" />
          );
        })}
      </g>
      
      {/* Sparkle emojis */}
      <text x={canvasWidth * 0.12} y={canvasHeight * 0.35} fontSize="28" opacity={0.8 + Math.sin(animTime / 200) * 0.2}>✨</text>
      <text x={canvasWidth * 0.88} y={canvasHeight * 0.35} fontSize="28" opacity={0.8 + Math.sin(animTime / 250) * 0.2}>✨</text>
      <text x={canvasWidth * 0.08} y={canvasHeight * 0.55} fontSize="22" opacity={0.7 + Math.sin(animTime / 300) * 0.2}>🌟</text>
      <text x={canvasWidth * 0.92} y={canvasHeight * 0.55} fontSize="22" opacity={0.7 + Math.sin(animTime / 280) * 0.2}>🌟</text>
      
      {/* Custom Bee SVG in center */}
      <image href="/HoneyCookingCompanion_bee.svg" x={canvasWidth / 2 - 40} y={canvasHeight * 0.32} width="80" height="80" />
      
      {/* Text */}
      <text x={canvasWidth / 2 + 2} y={canvasHeight * 0.22} textAnchor="middle" fill={COLORS.amberDark} fontSize="24" fontWeight="bold" fontFamily="'Fredoka', sans-serif" opacity="0.3">
        {levelNumber} Levels Complete!
      </text>
      <text x={canvasWidth / 2} y={canvasHeight * 0.22 - 2} textAnchor="middle" fill={COLORS.navy} fontSize="24" fontWeight="bold" fontFamily="'Fredoka', sans-serif">
        {levelNumber} Levels Complete!
      </text>
      
      <text x={canvasWidth / 2} y={canvasHeight * 0.58} textAnchor="middle" fill={MODE_CONFIG[modeName]?.color || COLORS.amber} fontSize="14" fontWeight="bold">
        {MODE_CONFIG[modeName]?.icon} {MODE_CONFIG[modeName]?.name} Mode
      </text>
      
      <text x={canvasWidth / 2} y={canvasHeight * 0.66} textAnchor="middle" fill={COLORS.navy} fontSize="14" opacity="0.7">
        Keep up the great dancing!
      </text>
      
      {/* Dancing bees */}
      {bees.map(bee => (
        <Bee key={bee.id} x={bee.x} y={bee.y} size={bee.size} angle={bee.angle} isWaggling={true} opacity={0.95} animTime={animTime} />
      ))}
      
      {/* Progress bar */}
      <rect x={canvasWidth * 0.15} y={canvasHeight - 50} width={canvasWidth * 0.7} height={10} rx={5} fill={COLORS.navy} opacity={0.15} />
      <rect x={canvasWidth * 0.15} y={canvasHeight - 50} width={canvasWidth * 0.7 * progress} height={10} rx={5} fill={COLORS.amber} />
    </svg>
  );
};

// =============================================================================
// MAIN GAME COMPONENT
// =============================================================================
const WaggleDanceGame = () => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Game state
  const [gameState, setGameState] = useState('menu');
  const [currentMode, setCurrentMode] = useState('easy');
  const [level, setLevel] = useState(0);
  const [score, setScore] = useState(0);
  const [totalLevelsCompleted, setTotalLevelsCompleted] = useState(0);
  const [highScores, setHighScores] = useState(() => {
    const saved = localStorage.getItem('waggle_high_scores');
    return saved ? JSON.parse(saved) : { easy: 0, medium: 0, difficult: 0 };
  });
  const [unlockedModes, setUnlockedModes] = useState(() => {
    const saved = localStorage.getItem('waggle_unlocked_modes');
    return saved ? JSON.parse(saved) : { easy: true, medium: false, difficult: false };
  });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 500 });
  
  // Game play state
  const [guideProgress, setGuideProgress] = useState(0);
  const [playerPath, setPlayerPath] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [levelResult, setLevelResult] = useState(null);
  const [guideAnimTime, setGuideAnimTime] = useState(0);
  const [canReplay, setCanReplay] = useState(false);
  
  const animationRef = useRef(null);
  const guideStartTime = useRef(null);
  const buzzIntervalRef = useRef(null);
  
  // Sync sound system with soundEnabled state
  useEffect(() => {
    sounds.setEnabled(soundEnabled);
  }, [soundEnabled]);
  
  // Initialize audio on first user interaction
  const initAudio = useCallback(() => {
    sounds.init();
  }, []);
  
  const getCurrentPatterns = useCallback(() => {
    return MODE_CONFIG[currentMode]?.patterns || EASY_PATTERNS;
  }, [currentMode]);
  
  // Resize handler
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const size = Math.min(rect.width - 32, 500);
        setCanvasSize({ width: size, height: size * 1.1 });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  
  const getScaledPoints = useCallback(() => {
    const patterns = getCurrentPatterns();
    if (!patterns[level]) return [];
    return patterns[level].points.map(p => ({
      x: p.x * canvasSize.width,
      y: p.y * canvasSize.height
    }));
  }, [level, canvasSize, getCurrentPatterns]);
  
  // Animate guide bee with buzz sound
  useEffect(() => {
    if (gameState !== 'playing' || !showGuide) {
      // Clear buzz interval when not showing guide
      if (buzzIntervalRef.current) {
        clearInterval(buzzIntervalRef.current);
        buzzIntervalRef.current = null;
      }
      return;
    }
    
    guideStartTime.current = Date.now();
    const patterns = getCurrentPatterns();
    const pattern = patterns[level];
    const duration = pattern?.guideSpeed || 3000;
    
    // Start gentle buzz sound at intervals
    buzzIntervalRef.current = setInterval(() => {
      sounds.playBuzz(0.15);
    }, 200);
    
    const animate = () => {
      const now = Date.now();
      const elapsed = now - guideStartTime.current;
      const progress = Math.min(elapsed / duration, 1);
      setGuideProgress(progress);
      setGuideAnimTime(now);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setShowGuide(false);
        setGuideProgress(0);
        setCanReplay(true);
        // Stop buzzing
        if (buzzIntervalRef.current) {
          clearInterval(buzzIntervalRef.current);
          buzzIntervalRef.current = null;
        }
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    return () => { 
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (buzzIntervalRef.current) {
        clearInterval(buzzIntervalRef.current);
        buzzIntervalRef.current = null;
      }
    };
  }, [gameState, showGuide, level, getCurrentPatterns]);
  
  // FIXED: Calculate accuracy - checks both proximity AND sequence order
  const calculateAccuracy = useCallback(() => {
    if (playerPath.length < 10) return 0;
    
    const patternPoints = getScaledPoints();
    if (patternPoints.length < 2) return 0;
    
    let totalScore = 0;
    let checks = 0;
    
    // Sample player path at regular intervals
    const sampleRate = Math.max(1, Math.floor(playerPath.length / 30));
    
    for (let i = 0; i < playerPath.length; i += sampleRate) {
      const playerPoint = playerPath[i];
      const playerProgress = i / playerPath.length; // 0 to 1
      
      // Find the expected pattern point at this progress
      const expectedIndex = Math.floor(playerProgress * (patternPoints.length - 1));
      const expectedPoint = patternPoints[expectedIndex];
      
      // Calculate distance to expected point (sequence matters!)
      const dist = Math.sqrt(
        Math.pow(playerPoint.x - expectedPoint.x, 2) +
        Math.pow(playerPoint.y - expectedPoint.y, 2)
      );
      
      // Also check nearby pattern points (allow some flexibility)
      let minDist = dist;
      for (let j = Math.max(0, expectedIndex - 3); j <= Math.min(patternPoints.length - 1, expectedIndex + 3); j++) {
        const nearbyDist = Math.sqrt(
          Math.pow(playerPoint.x - patternPoints[j].x, 2) +
          Math.pow(playerPoint.y - patternPoints[j].y, 2)
        );
        minDist = Math.min(minDist, nearbyDist);
      }
      
      const maxDist = 60; // pixels
      const pointScore = Math.max(0, 1 - minDist / maxDist);
      totalScore += pointScore;
      checks++;
    }
    
    return checks > 0 ? Math.round((totalScore / checks) * 100) : 0;
  }, [playerPath, getScaledPoints]);
  
  // Handle drawing
  const getCanvasPosition = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  
  const handleStart = (e) => {
    if (showGuide || gameState !== 'playing') return;
    e.preventDefault();
    const pos = getCanvasPosition(e);
    if (pos) {
      setIsDrawing(true);
      setPlayerPath([pos]);
      setCanReplay(false); // Hide replay button once drawing starts
    }
  };
  
  const handleMove = (e) => {
    if (!isDrawing || showGuide) return;
    e.preventDefault();
    const pos = getCanvasPosition(e);
    if (pos) setPlayerPath(prev => [...prev, pos]);
  };
  
  const handleEnd = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    
    const acc = calculateAccuracy();
    const patterns = getCurrentPatterns();
    const pattern = patterns[level];
    const passed = acc >= pattern.requiredAccuracy;
    const levelPoints = passed ? Math.round(acc * (level + 1) * 10) : 0;
    
    setLevelResult({ passed, accuracy: acc, points: levelPoints, required: pattern.requiredAccuracy });
    if (passed) {
      setScore(prev => prev + levelPoints);
      sounds.playSuccess();
    } else {
      sounds.playTryAgain();
    }
  };
  
  // Watch Again - replay the guide
  const handleWatchAgain = () => {
    sounds.playClick();
    setCanReplay(false);
    setPlayerPath([]);
    setShowGuide(true);
  };
  
  const startGame = (mode) => {
    initAudio();
    sounds.playClick();
    setCurrentMode(mode);
    setGameState('playing');
    setLevel(0);
    setScore(0);
    setTotalLevelsCompleted(0);
    setPlayerPath([]);
    setShowGuide(true);
    setLevelResult(null);
    setCanReplay(false);
  };
  
  const handleNextLevel = () => {
    setLevelResult(null);
    setPlayerPath([]);
    setCanReplay(false);
    
    const patterns = getCurrentPatterns();
    const newTotalCompleted = totalLevelsCompleted + 1;
    setTotalLevelsCompleted(newTotalCompleted);
    
    if (level + 1 >= patterns.length) {
      // Mode complete
      const newHighScores = { ...highScores };
      if (score > highScores[currentMode]) {
        newHighScores[currentMode] = score;
        setHighScores(newHighScores);
        localStorage.setItem('waggle_high_scores', JSON.stringify(newHighScores));
      }
      const newUnlocked = { ...unlockedModes };
      if (currentMode === 'easy') newUnlocked.medium = true;
      if (currentMode === 'medium') newUnlocked.difficult = true;
      setUnlockedModes(newUnlocked);
      localStorage.setItem('waggle_unlocked_modes', JSON.stringify(newUnlocked));
      setGameState('complete');
      sounds.playVictory(); // Triumphant victory fanfare!
    } else if (newTotalCompleted % 5 === 0) {
      setGameState('celebrating');
      sounds.playCelebration(); // Celebration melody
    } else {
      setLevel(prev => prev + 1);
      setShowGuide(true);
    }
  };
  
  const handleRetryLevel = () => {
    sounds.playClick();
    setLevelResult(null);
    setPlayerPath([]);
    setShowGuide(true);
    setCanReplay(false);
  };
  
  const handleCelebrationComplete = () => {
    setLevel(prev => prev + 1);
    setShowGuide(true);
    setGameState('playing');
  };
  
  const getGuideBeePosition = () => {
    const points = getScaledPoints();
    if (points.length === 0) return { x: 0, y: 0, angle: 0 };
    const index = Math.min(Math.floor(guideProgress * (points.length - 1)), points.length - 2);
    const t = (guideProgress * (points.length - 1)) % 1;
    const p1 = points[index];
    const p2 = points[Math.min(index + 1, points.length - 1)];
    const x = p1.x + (p2.x - p1.x) * t;
    const y = p1.y + (p2.y - p1.y) * t;
    const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI);
    return { x, y, angle };
  };
  
  const renderPatternPath = () => {
    const points = getScaledPoints();
    if (points.length < 2) return null;
    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    return (
      <path d={pathD} fill="none" stroke={MODE_CONFIG[currentMode]?.color || COLORS.amber} strokeWidth="3" strokeDasharray="8 4" opacity={showGuide ? 0.6 : 0.3} />
    );
  };
  
  const renderPlayerPath = () => {
    if (playerPath.length < 2) return null;
    const pathD = playerPath.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    return <path d={pathD} fill="none" stroke={COLORS.navy} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />;
  };
  
  const patterns = getCurrentPatterns();
  
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col" ref={containerRef}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-amber-200 px-4 py-3">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#1e3a5f] hover:text-amber-600 transition-colors" data-testid="back-button">
            <ArrowLeft size={20} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <h1 className="text-lg font-bold text-[#1e3a5f] flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            <img src="/HoneyCookingCompanion_bee.svg" alt="Bee" className="w-6 h-6" />
            Waggle Dance
          </h1>
          <button onClick={() => setSoundEnabled(!soundEnabled)} className="p-2 text-[#1e3a5f] hover:text-amber-600 transition-colors" data-testid="sound-toggle">
            {soundEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </button>
        </div>
      </header>
      
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-6">
        {/* Score Display */}
        {(gameState === 'playing' || gameState === 'celebrating') && (
          <div className="flex items-center justify-between w-full max-w-md mb-4">
            <div className="flex items-center gap-2">
              <Star className="text-amber-600" size={20} />
              <span className="font-bold text-[#1e3a5f]">{score}</span>
            </div>
            <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${MODE_CONFIG[currentMode]?.color}20`, color: MODE_CONFIG[currentMode]?.color }}>
              {MODE_CONFIG[currentMode]?.icon} {MODE_CONFIG[currentMode]?.name}
            </span>
            <span className="text-sm text-gray-500">Level {level + 1}/{patterns.length}</span>
          </div>
        )}
        
        {/* Game Canvas */}
        <div className="relative bg-white rounded-2xl shadow-lg overflow-hidden" style={{ width: canvasSize.width, height: canvasSize.height }}>
          
          {/* Menu */}
          {gameState === 'menu' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <img src="/HoneyCookingCompanion_bee.svg" alt="Bee" className="w-20 h-20 mb-4" />
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>Waggle Dance</h2>
              <p className="text-gray-600 mb-6 max-w-xs">Watch the bee trace a pattern, then trace it yourself in the same order!</p>
              <button onClick={() => { initAudio(); sounds.playClick(); setGameState('modeSelect'); }} className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-full font-semibold transition-all hover:scale-105 shadow-lg" data-testid="start-game-btn">
                <Play size={20} /> Start Dancing
              </button>
            </div>
          )}
          
          {/* Mode Select */}
          {gameState === 'modeSelect' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <h2 className="text-xl font-bold text-[#1e3a5f] mb-6" style={{ fontFamily: "'Fredoka', sans-serif" }}>Choose Difficulty</h2>
              <div className="space-y-3 w-full max-w-xs">
                {Object.entries(MODE_CONFIG).map(([mode, config]) => {
                  const isUnlocked = unlockedModes[mode];
                  return (
                    <button key={mode} onClick={() => isUnlocked && startGame(mode)} disabled={!isUnlocked}
                      className={`w-full flex items-center justify-between px-5 py-4 rounded-xl font-semibold transition-all ${isUnlocked ? 'bg-white border-2 hover:scale-102 hover:shadow-md' : 'bg-gray-100 border-2 border-gray-200 cursor-not-allowed opacity-60'}`}
                      style={{ borderColor: isUnlocked ? config.color : undefined }} data-testid={`mode-${mode}-btn`}>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{config.icon}</span>
                        <div className="text-left">
                          <div style={{ color: isUnlocked ? config.color : '#9ca3af' }}>{config.name}</div>
                          {highScores[mode] > 0 && <div className="text-xs text-gray-400">Best: {highScores[mode]}</div>}
                        </div>
                      </div>
                      {isUnlocked ? <Unlock size={20} style={{ color: config.color }} /> : <Lock size={20} className="text-gray-400" />}
                    </button>
                  );
                })}
              </div>
              <button onClick={() => setGameState('menu')} className="mt-6 text-gray-500 hover:text-amber-600 transition-colors text-sm">← Back</button>
            </div>
          )}
          
          {/* Playing */}
          {gameState === 'playing' && (
            <>
              <svg ref={canvasRef} width={canvasSize.width} height={canvasSize.height}
                onMouseDown={handleStart} onMouseMove={handleMove} onMouseUp={handleEnd} onMouseLeave={handleEnd}
                onTouchStart={handleStart} onTouchMove={handleMove} onTouchEnd={handleEnd}
                style={{ touchAction: 'none', cursor: showGuide ? 'default' : 'crosshair' }} data-testid="game-canvas">
                <defs>
                  <radialGradient id="gameGlow" cx="50%" cy="50%" r="70%">
                    <stop offset="0%" stopColor={COLORS.amberLight} stopOpacity="0.1" />
                    <stop offset="100%" stopColor={COLORS.background} stopOpacity="0" />
                  </radialGradient>
                </defs>
                <rect width={canvasSize.width} height={canvasSize.height} fill="url(#gameGlow)" />
                {renderPatternPath()}
                {renderPlayerPath()}
                {showGuide && <Bee {...getGuideBeePosition()} size={35} isWaggling={true} animTime={guideAnimTime} />}
              </svg>
              
              {/* Instructions */}
              {showGuide && (
                <div className="absolute top-4 left-0 right-0 text-center">
                  <span className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium">Watch the bee! 🐝</span>
                </div>
              )}
              {!showGuide && !levelResult && (
                <div className="absolute top-4 left-0 right-0 text-center">
                  <span className="bg-[#1e3a5f] text-white px-4 py-2 rounded-full text-sm font-medium">Your turn! Trace the pattern</span>
                </div>
              )}
              
              {/* Watch Again button */}
              {canReplay && !levelResult && (
                <div className="absolute top-16 left-0 right-0 text-center">
                  <button onClick={handleWatchAgain} className="inline-flex items-center gap-2 bg-white border-2 border-amber-400 text-amber-600 px-4 py-2 rounded-full text-sm font-medium hover:bg-amber-50 transition-colors shadow-md" data-testid="watch-again-btn">
                    <Eye size={16} /> Watch Again
                  </button>
                </div>
              )}
              
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="text-gray-400 text-sm">{patterns[level]?.name}</span>
              </div>
            </>
          )}
          
          {/* Celebration */}
          {gameState === 'celebrating' && (
            <CelebrationScene canvasWidth={canvasSize.width} canvasHeight={canvasSize.height} onComplete={handleCelebrationComplete} levelNumber={totalLevelsCompleted} modeName={currentMode} />
          )}
          
          {/* Complete */}
          {gameState === 'complete' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-gradient-to-b from-amber-50 to-white">
              <div className="text-6xl mb-4">🏆</div>
              <h2 className="text-2xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>{MODE_CONFIG[currentMode]?.name} Complete!</h2>
              <p className="text-gray-600 mb-2">You mastered all {patterns.length} patterns!</p>
              <div className="text-3xl font-bold text-amber-600 mb-4">{score} points</div>
              {score >= highScores[currentMode] && score > 0 && (
                <div className="bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">🎉 New High Score!</div>
              )}
              {currentMode === 'easy' && unlockedModes.medium && (
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-4">🔓 Medium Unlocked!</div>
              )}
              {currentMode === 'medium' && unlockedModes.difficult && (
                <div className="bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-medium mb-4">🔓 Difficult Unlocked!</div>
              )}
              <div className="flex flex-col gap-2 w-full max-w-xs">
                <button onClick={() => startGame(currentMode)} className="flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-full font-semibold transition-all hover:scale-105 shadow-lg" data-testid="play-again-btn">
                  <RotateCcw size={20} /> Play Again
                </button>
                <button onClick={() => setGameState('modeSelect')} className="text-gray-500 hover:text-amber-600 transition-colors text-sm py-2">Choose Different Mode</button>
              </div>
            </div>
          )}
        </div>
        
        {/* Level Result Modal */}
        {levelResult && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
              <div className="text-5xl mb-4">{levelResult.passed ? '🎉' : '😅'}</div>
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>{levelResult.passed ? 'Great Dance!' : 'Not Quite!'}</h3>
              
              <div className="my-4">
                <div className="text-sm text-gray-500 mb-1">Accuracy</div>
                <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                  <div className={`absolute inset-y-0 left-0 ${levelResult.passed ? 'bg-green-500' : 'bg-amber-600'} transition-all duration-500`} style={{ width: `${levelResult.accuracy}%` }} />
                  <div className="absolute inset-y-0 w-0.5 bg-[#1e3a5f]" style={{ left: `${levelResult.required}%` }} />
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className={levelResult.passed ? 'text-green-600' : 'text-amber-600'}>{levelResult.accuracy}%</span>
                  <span className="text-gray-400">Need: {levelResult.required}%</span>
                </div>
              </div>
              
              {levelResult.passed && <div className="text-amber-600 font-bold text-lg mb-4">+{levelResult.points} points!</div>}
              
              <div className="flex gap-3">
                {!levelResult.passed && (
                  <button onClick={handleRetryLevel} className="flex-1 flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-[#1e3a5f] px-4 py-3 rounded-full font-medium transition-colors" data-testid="retry-btn">
                    <RotateCcw size={18} /> Retry
                  </button>
                )}
                <button onClick={levelResult.passed ? handleNextLevel : handleRetryLevel} className="flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-3 rounded-full font-medium transition-colors" data-testid="continue-btn">
                  {levelResult.passed ? (<><Play size={18} /> {level + 1 >= patterns.length ? 'Finish' : 'Next Level'}</>) : (<><RotateCcw size={18} /> Try Again</>)}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default WaggleDanceGame;
